/**
 * Picks View Utilities
 * Helper functions for the picks feature
 */

import type { GeneratedLeg, GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { formatRelativeDay } from '@parlay-picker/shared/formatting';
import type { LeagueKey } from '@parlay-picker/shared/league';
import {
  findPlayerHeadshotUrl,
  playerInitialsAvatarUrl,
} from '@parlay-picker/shared/playerHeadshotUrl';
import { formatPropString } from '@parlay-picker/shared/statLabels';
import { findTeam } from '@parlay-picker/shared/teams';
import { WNBA_PLAYER_IDS } from '@parlay-picker/shared/wnbaPlayerIds';
import { useSyncExternalStore } from 'react';
import { type AnalysisConfig, NBA_PLAYER_IDS, type RiskTier } from './constants';

// =============================================================================
// Types — Parlay/Leg/Analysis types live in packages/shared/src/api/picksApi.ts
// =============================================================================

export interface Game {
  id?: string;
  sport_key: LeagueKey;
  sport_title?: string;
  commence_time: string;
  away_team?: string;
  home_team?: string;
  [key: string]: unknown;
}

// Team game stats for game props (spreads, totals, moneyline)
export interface TeamGameStats {
  date?: string;
  opponent?: string;
  opponentAbbr?: string;
  isHome: boolean;
  teamScore: number;
  opponentScore: number;
  margin: number; // teamScore - opponentScore (positive = win)
  gameTotal: number; // teamScore + opponentScore
  won: boolean;
}

export interface GroupedGames {
  label: string;
  games: Game[];
}

export interface ParsedProp {
  propLine: number | null;
  statKey: string | null;
}

export interface AnalyzerLeg {
  playerName: string;
  propType: string;
  overUnder: string;
  line: number | null;
  raw: string;
}

export interface RiskTierGroup extends RiskTier {
  parlays: GeneratedParlay[];
}

// =============================================================================
// Functions
// =============================================================================

/**
 * Get confidence color class based on confidence level
 */
export function getConfidenceColor(confidence: string | undefined): string {
  return confidence === 'High'
    ? 'text-green-400'
    : confidence === 'Medium'
      ? 'text-yellow-400'
      : 'text-orange-400';
}

/**
 * Format time from ISO string to locale time
 */
export function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get the date key for a game (used for same-day validation)
 */
export function getGameDateKey(gameStartsAt: string): string {
  return new Date(gameStartsAt).toDateString();
}

/**
 * Group games by day
 */
export function groupGamesByDay(games: Game[]): GroupedGames[] {
  const grouped: Record<string, GroupedGames> = {};
  games.forEach((game) => {
    const dateKey = new Date(game.commence_time).toDateString();
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        label: formatRelativeDay(game.commence_time),
        games: [],
      };
    }
    grouped[dateKey].games.push(game);
  });
  return Object.values(grouped);
}

// Dynamic player ID maps: starts with hardcoded fallback, updated with API data on app load.
// Uses useSyncExternalStore subscription so React components re-render when the map updates.
// SessionStorage cache prevents HMR resets from losing the fetched map during development.
const SESSION_KEY = 'playerIdMap';
const SESSION_KEY_WNBA = 'wnbaPlayerIdMap';

function loadCachedPlayerIds(): Record<string, number> {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) return { ...NBA_PLAYER_IDS, ...JSON.parse(cached) };
  } catch {
    // sessionStorage unavailable or corrupt — use hardcoded fallback
  }
  return NBA_PLAYER_IDS;
}

function loadCachedWnbaPlayerIds(): Record<string, number> {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY_WNBA);
    if (cached) return { ...WNBA_PLAYER_IDS, ...JSON.parse(cached) };
  } catch {
    // sessionStorage unavailable or corrupt — use hardcoded fallback
  }
  return WNBA_PLAYER_IDS;
}

let activePlayerIdMap: Record<string, number> = loadCachedPlayerIds();
let activeWnbaPlayerIdMap: Record<string, number> = loadCachedWnbaPlayerIds();
let mapVersion = 0;
const mapListeners = new Set<() => void>();

function subscribeToPlayerIdMap(listener: () => void): () => void {
  mapListeners.add(listener);
  return () => mapListeners.delete(listener);
}

function getPlayerIdMapVersion(): number {
  return mapVersion;
}

interface PlayerIdMapsResponse {
  nba: Record<string, number>;
  wnba: Record<string, number>;
}

function isPlayerIdMapsResponse(data: unknown): data is PlayerIdMapsResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'nba' in data &&
    'wnba' in data &&
    typeof (data as PlayerIdMapsResponse).nba === 'object' &&
    typeof (data as PlayerIdMapsResponse).wnba === 'object'
  );
}

/**
 * Update the player ID maps with dynamically fetched data from the NBA Stats API.
 * Called once on app startup. Accepts the new { nba, wnba } response shape,
 * or a flat Record for backwards compatibility.
 */
export function setPlayerIdMap(dynamicMap: Record<string, number> | PlayerIdMapsResponse): void {
  if (isPlayerIdMapsResponse(dynamicMap)) {
    // New shape: { nba: {...}, wnba: {...} }
    activePlayerIdMap = { ...NBA_PLAYER_IDS, ...dynamicMap.nba };
    activeWnbaPlayerIdMap = { ...WNBA_PLAYER_IDS, ...dynamicMap.wnba };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(dynamicMap.nba));
      sessionStorage.setItem(SESSION_KEY_WNBA, JSON.stringify(dynamicMap.wnba));
    } catch {
      // sessionStorage full or unavailable — non-critical
    }
  } else {
    // Legacy flat shape (backwards compat)
    activePlayerIdMap = { ...NBA_PLAYER_IDS, ...dynamicMap };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(dynamicMap));
    } catch {
      // non-critical
    }
  }
  mapVersion++;
  for (const listener of mapListeners) listener();
}

/**
 * Player headshot URL from the player's own league, or an initials image when that league has none
 * for the name. Null for no name, or no league (chat cards saved before they carried one).
 */
export function getPlayerHeadshotUrl(
  playerName: string | null | undefined,
  leagueKey: LeagueKey | null,
): string | null {
  if (!playerName || !leagueKey) return null;
  return (
    findPlayerHeadshotUrl(playerName, leagueKey, {
      basketball_nba: activePlayerIdMap,
      basketball_wnba: activeWnbaPlayerIdMap,
    }) ?? playerInitialsAvatarUrl(playerName)
  );
}

/** Re-renders the caller when the player ID maps update (the API fetch on startup). */
export function usePlayerIdMapVersion(): number {
  return useSyncExternalStore(subscribeToPlayerIdMap, getPlayerIdMapVersion);
}

/** getPlayerHeadshotUrl that re-renders when the player ID maps update. */
export function usePlayerHeadshotUrl(
  playerName: string | null | undefined,
  leagueKey: LeagueKey | null,
): string | null {
  usePlayerIdMapVersion();
  return getPlayerHeadshotUrl(playerName, leagueKey);
}

/**
 * Parse prop string to extract the line value and stat key for bar chart
 */
export function parsePropForChart(prop: string | undefined): ParsedProp {
  if (!prop) return { propLine: null, statKey: null };

  const propLower = prop.toLowerCase();

  // Extract the number (line) from the prop string, preserving sign for spreads
  const numberMatch = prop.match(/(-?\d+\.?\d*)/);
  const propLine = numberMatch ? parseFloat(numberMatch[1]) : null;

  // Helper to check if prop contains any of the given keywords; "3-Pointers" also contains "point"
  const hasThrees =
    propLower.includes('3-pointer') || propLower.includes('three') || propLower.includes('3pm');
  const hasPoints = !hasThrees && (propLower.includes('point') || propLower.includes('pts'));
  const hasRebounds = propLower.includes('rebound') || propLower.includes('reb');
  const hasAssists = propLower.includes('assist') || propLower.includes('ast');

  // Determine the stat key based on prop type
  // Check combo props first (they contain multiple stat types)
  let statKey: string | null = null;
  if (hasPoints && hasRebounds && hasAssists) {
    statKey = 'pra';
  } else if (hasPoints && hasRebounds && !hasAssists) {
    statKey = 'pr';
  } else if (hasPoints && hasAssists && !hasRebounds) {
    statKey = 'pa';
  } else if (hasRebounds && hasAssists && !hasPoints) {
    statKey = 'ra';
  } else if (
    (propLower.includes('block') || propLower.includes('blk')) &&
    (propLower.includes('steal') || propLower.includes('stl'))
  ) {
    statKey = 'bs';
  } else if (hasPoints) {
    statKey = 'pts';
  } else if (hasRebounds) {
    statKey = 'reb';
  } else if (hasAssists) {
    statKey = 'ast';
  } else if (hasThrees) {
    statKey = 'fg3m';
  } else if (propLower.includes('turnover')) {
    statKey = 'turnover';
  } else if (propLower.includes('block') || propLower.includes('blk')) {
    statKey = 'blk';
  } else if (propLower.includes('steal') || propLower.includes('stl')) {
    statKey = 'stl';
  }

  return { propLine, statKey };
}

/**
 * Format prop text to use full words (converts abbreviations to full words)
 * Also strips _alternate suffix since alternate props are indicated by line format (X+)
 */
export function formatPropDisplay(prop: string | undefined): string | undefined {
  return prop ? formatPropString(prop) : prop;
}

/**
 * Find opponent team for a given team from today's games
 */
export function findOpponent(
  teamName: string | null | undefined,
  games: Game[],
  leagueKey: LeagueKey,
): string | null {
  if (!teamName) return null;
  // Resolve input to a canonical team entry (handles abbreviations, full names, partials)
  const resolved = findTeam(teamName, leagueKey);
  const resolvedAbbr = resolved?.abbr;

  for (const game of games) {
    const awayInfo = findTeam(game.away_team, leagueKey);
    const homeInfo = findTeam(game.home_team, leagueKey);
    if (!awayInfo || !homeInfo) continue;

    if (resolvedAbbr === awayInfo.abbr) {
      return homeInfo.abbr;
    }
    if (resolvedAbbr === homeInfo.abbr) {
      return awayInfo.abbr;
    }
  }
  return null;
}

// Re-export shared filter/sort logic (single source of truth for web + mobile)
export { filterParlays, parseOdds } from '@parlay-picker/shared/parlayFilters';

/**
 * Group parlays by risk tier
 */
export function groupByRiskTier(
  filteredRecs: GeneratedParlay[],
  riskTiers: RiskTier[],
  sortBy: string,
): RiskTierGroup[] {
  return riskTiers.map((tier) => ({
    ...tier,
    parlays: filteredRecs
      .filter((r) => r.name?.toLowerCase().includes(tier.key))
      .sort((a, b) => (sortBy === 'default' ? (a.rank || 1) - (b.rank || 1) : 0)),
  }));
}

/**
 * Deep merge config objects - ensures all default keys exist in saved config
 * This handles cases where new config keys were added after user saved their config
 */
export function deepMergeConfig(
  defaults: AnalysisConfig,
  saved: Partial<AnalysisConfig> | null,
): AnalysisConfig {
  if (!saved) return defaults;

  const result = { ...defaults };

  for (const key of Object.keys(defaults) as (keyof AnalysisConfig)[]) {
    const defaultValue = defaults[key];
    const savedValue = saved[key];

    if (savedValue !== undefined) {
      // If both are objects (but not arrays), deep merge them
      if (
        typeof defaultValue === 'object' &&
        defaultValue !== null &&
        !Array.isArray(defaultValue) &&
        typeof savedValue === 'object' &&
        savedValue !== null &&
        !Array.isArray(savedValue)
      ) {
        result[key] = {
          ...defaultValue,
          ...savedValue,
        } as AnalysisConfig[typeof key];
      } else {
        result[key] = savedValue as AnalysisConfig[typeof key];
      }
    }
  }

  return result;
}

/**
 * Convert AI picks leg format to analyzer input format
 * AI picks format: { player, team, prop: "Over 25.5 pts", odds }
 * Analyzer format: { playerName, propType, overUnder, line }
 */
export function convertPicksLegToAnalyzerFormat(
  leg: GeneratedLeg | null | undefined,
): AnalyzerLeg | null {
  if (!leg?.prop) return null;

  const prop = leg.prop;
  const propLower = prop.toLowerCase();

  // Extract over/under
  let overUnder = 'Over';
  if (propLower.startsWith('under')) {
    overUnder = 'Under';
  }

  // Extract line (number) from prop string
  const lineMatch = prop.match(/(\d+\.?\d*)/);
  const line = lineMatch ? parseFloat(lineMatch[1]) : null;

  // Extract prop type from the prop string
  let propType = 'points'; // default

  // Check for combo props first
  if (
    (propLower.includes('pts') || propLower.includes('point')) &&
    (propLower.includes('reb') || propLower.includes('rebound')) &&
    (propLower.includes('ast') || propLower.includes('assist'))
  ) {
    propType = 'pts+reb+ast';
  } else if (
    (propLower.includes('pts') || propLower.includes('point')) &&
    (propLower.includes('reb') || propLower.includes('rebound'))
  ) {
    propType = 'pts+reb';
  } else if (
    (propLower.includes('pts') || propLower.includes('point')) &&
    (propLower.includes('ast') || propLower.includes('assist'))
  ) {
    propType = 'pts+ast';
  } else if (
    (propLower.includes('reb') || propLower.includes('rebound')) &&
    (propLower.includes('ast') || propLower.includes('assist'))
  ) {
    propType = 'reb+ast';
  } else if (
    (propLower.includes('blk') || propLower.includes('block')) &&
    (propLower.includes('stl') || propLower.includes('steal'))
  ) {
    propType = 'blk+stl';
    // Single stat props; threes first, since "3-Pointers" also contains "point"
  } else if (
    propLower.includes('3-pointer') ||
    propLower.includes('three') ||
    propLower.includes('3pm') ||
    propLower.includes('3pt')
  ) {
    propType = 'threes';
  } else if (propLower.includes('point') || propLower.includes('pts')) {
    propType = 'points';
  } else if (propLower.includes('rebound') || propLower.includes('reb')) {
    propType = 'rebounds';
  } else if (propLower.includes('assist') || propLower.includes('ast')) {
    propType = 'assists';
  } else if (propLower.includes('block') || propLower.includes('blk')) {
    propType = 'blocks';
  } else if (propLower.includes('steal') || propLower.includes('stl')) {
    propType = 'steals';
  } else if (propLower.includes('turnover') || propLower.includes('to')) {
    propType = 'turnovers';
  } else if (propLower.includes('field goal') || propLower.includes('fg')) {
    propType = 'field goals';
  } else if (propLower.includes('free throw') || propLower.includes('ft')) {
    propType = 'free throws';
  }

  return {
    playerName: leg.player || '',
    propType,
    overUnder,
    line,
    raw: `${leg.player} ${overUnder} ${line} ${propType}`,
  };
}

/**
 * Convert a full parlay recommendation to analyzer text format
 */
export function convertParlayToAnalyzerText(rec: GeneratedParlay | null | undefined): string {
  if (!rec?.legs?.length) return '';

  return rec.legs
    .map((leg) => {
      const converted = convertPicksLegToAnalyzerFormat(leg);
      return converted ? converted.raw : '';
    })
    .filter(Boolean)
    .join('\n');
}

// =============================================================================
// Strategy Detection (Parlay-Level)
// =============================================================================

// NOTE: Static player lists (OPTIMAL_ASSISTS_PLAYERS, AVOID_PLAYERS) have been removed.
// Player performance is now calculated dynamically from the database via strategyStatsService.
// Use the useStrategyStats hook to get dynamic player warnings/highlights.

/**
 * Check if a leg is an assists over pick
 */
export function isAssistsOverLeg(leg: GeneratedLeg): boolean {
  const statType = leg.statType?.toLowerCase() || '';
  const propLower = leg.prop?.toLowerCase() || '';
  const isAssists =
    statType === 'ast' ||
    statType === 'assists' ||
    propLower.includes('assist') ||
    propLower.includes('ast');
  const isOver = leg.overUnder === 'Over' || propLower.startsWith('over');

  return isAssists && isOver;
}

/**
 * Check if a leg has a strong edge (from AI analysis)
 */
export function hasStrongEdge(leg: GeneratedLeg): boolean {
  // Check analysis.edge field
  const edgeStr = leg.analysis?.edge?.toLowerCase() || '';
  if (edgeStr === 'strong') return true;

  // Check explanation.primaryEdge
  const primaryEdge = leg.explanation?.primaryEdge?.edge;
  if (primaryEdge && primaryEdge > 0) return true;

  return false;
}

// NOTE: analyzeStrategyMatch and StrategyMatch have been removed.
// All parlay scoring is now done dynamically via useStrategyStats hook
// which calculates player performance, prop type performance, and pattern
// analysis from the database of settled parlays.

// =============================================================================
// Builder Conversion — moved to @parlay-picker/shared/builderLeg
// =============================================================================
